import type { ComputedRef } from 'vue'
import { ExperimentValue, type UseExperimentReturn, type UseExperimentReturnReadonly, type ExperimentOptions, type ExperimentNameUnion, type CoreExperiment, type ExperimentAssignment } from '../types'
import { useCookie, useState, useDayjs, useCoreConfig, useCoreLogger, useUserInfo, readonly, computed, experimentRun, formatExperimentCookie, parseExperimentCookie } from '#imports'

/** `groupSize` consecutive user IDs share a bucket: 1 → A,B,A,B; 2 → A,A,B,B; … */
export function selectExperimentByUserId(userId: number | string, groupSize = 1): ExperimentValue {
    return Math.floor(Number(userId) / groupSize) % 2 === 0
        ? ExperimentValue.A
        : ExperimentValue.B
}

function useExperimentSource(cookieName: ExperimentOptions['cookieName'], experimentName: ExperimentNameUnion) {
    const { env, experiments } = useCoreConfig()
    const state = useState<ExperimentValue>(cookieName)

    const experiment = (experiments as CoreExperiment[] | undefined)?.find(e => e.name === experimentName)
    const versions = experiment?.versions
    // A concluded test keeps only its winner, which everyone sees; the middleware draws no one for it.
    const onlyVersion = versions && Object.keys(versions).length === 1 ? Object.keys(versions)[0] as ExperimentValue : undefined
    const isUserIdBased = experiment?.assignmentMethod === 'userId'

    const userInfo = isUserIdBased || experiment?.needsAuthentication ? useUserInfo() : undefined

    const userIdBucket = computed<ExperimentValue | undefined>(() => {
        if (!isUserIdBased) return undefined

        const userId = userInfo?.data.value?.id
        if (userId === undefined || userId === null) return undefined

        return selectExperimentByUserId(userId, experiment?.groupSize ?? 1)
    })

    const dayjs = useDayjs()
    const cookie = useCookie<string | null | undefined>(cookieName, {
        sameSite: 'lax',
        maxAge: dayjs.duration({ days: 30 }).asSeconds(), // 30 days
        path: '/',
        secure: true,
        httpOnly: false,
    })

    // Read-only consumers derive from state on SSR and from the cookie on the
    // client:
    //   - SSR: each `useCookie` call creates a fresh ref that reads request
    //     headers, so the middleware's write-mode cookie.value is invisible
    //     to a separate readonly call within the same request. `useState`
    //     IS shared by key across calls, so it reliably reflects the
    //     middleware's write within the render.
    //   - Client: the cookie is the live source of truth — it picks up
    //     middleware writes (via Set-Cookie → document.cookie), the
    //     cleanup middleware clearing retired cookies, and manual DevTools
    //     edits, all without re-entering write mode.
    // `state` is never written from the readonly path, so the original
    // poisoning bug (state stuck on 'a' from a pre-auth read) stays fixed.
    const current = computed<ExperimentValue | null>(() => {
        if (onlyVersion) return onlyVersion
        if (userIdBucket.value) return userIdBucket.value
        if (import.meta.server) return state.value ?? null

        return versions ? parseExperimentCookie(cookie.value, versions) : null
    })

    return { env, state, experiment, versions, onlyVersion, isUserIdBased, userInfo, userIdBucket, cookie, current }
}

/**
 * Composable for reading or writing A/B experiment group assignments.
 *
 * `useExperiment` owns assignment; the experiments middleware only decides
 * when to call write mode. `assignmentMethod: 'userId'` derives the bucket
 * from the logged-in user's ID, which is authoritative over the cookie.
 * `needsAuthentication` assigns nothing while logged out unless a prior
 * cookie exists.
 *
 * **Read-only mode** (default): returns a readonly computed. Resolves the
 * userId bucket first (available before any cookie round-trip), then
 * `useState` on the server / the live cookie on the client, then `'a'`.
 * This is the mode components should use to render. Tracking uses
 * `useExperimentAssignment`, which has no `'a'` fallback.
 *
 * **Write mode** (`allowWrite: true`): selects a group and persists it to
 * state and a cookie (30-day TTL). Only the experiment middleware should use
 * this mode. It must be the only `useCookie` ref writing the cookie — a
 * second ref for the same name created in the same tick cannot see the
 * first ref's write and would overwrite it. `override` (from
 * `?experiment=<name>:<arm>`) wins over the cookie and the random draw, but
 * not over a userId bucket.
 *
 * **Cookie value:** `<arm>.<test number>`, e.g. `b.31`, so bumping the test
 * number re-draws everyone instead of keeping their arm. A bare `b` counts
 * for the current test (set by hand, or written before this format).
 *
 * **Cookie config:** `sameSite: 'lax'`, `maxAge: 30 days`, `path: '/'`,
 * `secure: true`, `httpOnly: false`
 *
 * @param options - Experiment identification and write permission
 * @returns Ref containing the assigned group (`'a'`, `'b'`, etc.)
 *
 * @example
 * // Reading in a component (readonly)
 * const experiment = useExperiment({
 *     cookieName: ExperimentCookieName.LANDING_PAGE,
 *     experimentName: ExperimentName.LANDING_PAGE,
 * })
 * // experiment.value === 'a' | 'b'
 *
 * @example
 * // Writing in middleware
 * useExperiment({
 *     cookieName: experiment.cookieName,
 *     experimentName: experiment.name,
 *     allowWrite: true,
 *     override: experimentOverride(to.query, experiment.name),
 * })
 */
export function useExperiment(options: ExperimentOptions & { allowWrite: true, experimentName: ExperimentNameUnion }): UseExperimentReturn
export function useExperiment(options: ExperimentOptions & { allowWrite?: false }): UseExperimentReturnReadonly
export function useExperiment(options: ExperimentOptions): unknown {
    const { cookieName, experimentName, allowWrite, override } = options
    const { env, state, experiment, versions, isUserIdBased, userInfo, userIdBucket, cookie, current } = useExperimentSource(cookieName, experimentName)

    const logger = useCoreLogger().withTag('useExperiment')

    if (!allowWrite) {
        return readonly(computed(() => current.value ?? ExperimentValue.A))
    }

    const forced = versions && override && !userIdBucket.value ? parseExperimentCookie(override, versions) : null

    if (versions && forced) {
        cookie.value = formatExperimentCookie(forced, versions)
        state.value = forced

        return state
    }

    if (state.value && (!userIdBucket.value || userIdBucket.value === state.value)) {
        if (userIdBucket.value && versions) {
            const bucketCookie = formatExperimentCookie(userIdBucket.value, versions)

            if (cookie.value !== bucketCookie) cookie.value = bucketCookie
        }

        return state
    }

    const fromCookie = versions ? parseExperimentCookie(cookie.value, versions) : null

    function selectExperiment(experimentName: ExperimentNameUnion): ExperimentValue | undefined {
        if (!versions) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        const versionKeys = Object.keys(versions || {}) as ExperimentValue[]

        if (!versionKeys?.length) {
            if (env === 'dev') {
                logger.error(`Missing experiment: "${experimentName}" from the "core" module in "nuxt.config.ts"`)
            }

            return ExperimentValue.A
        }

        if (versionKeys.length === 1) {
            const firstVersionKey = versionKeys[0] as ExperimentValue

            if (env === 'dev') {
                logger.error(`Experiment has only set one option for the experiment: "${experimentName}" in the "core" module in "nuxt.config.ts". The experiment value will be set to: "${firstVersionKey}"`)
            }

            return firstVersionKey
        }

        if (env === 'dev' && versionKeys.some(key => versions[key]?.split('_').pop() !== experimentRun(versions))) {
            logger.error(`Experiment "${experimentName}" has versions with different test numbers in "nuxt.config.ts". Visitors are re-drawn when the test number of version A changes: "${experimentRun(versions)}"`)
        }

        if (userIdBucket.value) return userIdBucket.value

        if (isUserIdBased) return fromCookie ?? undefined

        if (experiment?.needsAuthentication && !userInfo?.isLoggedIn.value) {
            return fromCookie ?? undefined
        }

        if (fromCookie) return fromCookie

        const randomNumber = Math.floor(Math.random() * versionKeys.length)

        return versionKeys?.[randomNumber] as ExperimentValue
    }

    const selected = selectExperiment(experimentName)

    if (!selected) return state

    state.value = selected

    if (versions) {
        cookie.value = formatExperimentCookie(selected, versions)
    }

    return state
}

/**
 * The arm and version tracking counts this visitor in. Null when they aren't in a test: never
 * assigned (e.g. cookies blocked), the experiment is off, or only its winner remains. `useExperiment`
 * still renders A (or the winner) for them; crediting those visitors to an arm would skew the split.
 */
export function useExperimentAssignment(options: Pick<ExperimentOptions, 'cookieName' | 'experimentName'>): ComputedRef<ExperimentAssignment | null> {
    const { versions, onlyVersion, current } = useExperimentSource(options.cookieName, options.experimentName)

    return computed(() => {
        if (!current.value || onlyVersion) return null

        const version = versions?.[current.value]

        return version ? { variant: current.value, version } : null
    })
}
